import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Check, ChevronRight, ChevronLeft, AlertCircle, Loader, CheckCircle, Mail, Lock, User, Phone, MapPin } from 'lucide-react';
import api from '../../api/apiClient';

interface AbnResult {
  verified: boolean;
  abn?: string;
  entityName?: string;
  entityType?: string;
  businessStatus?: string;
  gstRegistered?: boolean;
  error?: string;
}

interface SubscriptionPlan {
  id: number;
  name: string;
  displayName: string;
  minLeases: number;
  maxLeases: number | null;
  pricePerLease: number;
  description: string;
}

interface DiscountCodeResult {
  valid: boolean;
  description?: string;
  fixedPricePerLease?: number;
  maxLeasesCap?: number;
  error?: string;
}

export default function AgencyRegistration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Step 1: ABN Verification
  const [abn, setAbn] = useState('');
  const [abnResult, setAbnResult] = useState<AbnResult | null>(null);
  const [abnVerified, setAbnVerified] = useState(false);

  // Step 2: Business Details
  const [agencyName, setAgencyName] = useState('');
  const [address, setAddress] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [emailValid, setEmailValid] = useState<boolean | null>(null);
  const [emailMessage, setEmailMessage] = useState('');

  // Step 3: Subscription Plan
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [enterpriseCode, setEnterpriseCode] = useState(searchParams.get('code') || '');
  const [discountResult, setDiscountResult] = useState<DiscountCodeResult | null>(null);
  const [useEnterpriseCode, setUseEnterpriseCode] = useState(!!searchParams.get('code'));

  // Step 4: Admin Account
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    loadSubscriptionPlans();

    // Check for enterprise code in URL
    const code = searchParams.get('code');
    if (code) {
      setEnterpriseCode(code);
      setUseEnterpriseCode(true);
      validateEnterpriseCode(code);
    }
  }, []);

  const loadSubscriptionPlans = async () => {
    try {
      const response = await api.get<SubscriptionPlan[]>('/agency/subscription-plans');
      setPlans(response);
      if (response.length > 0) {
        setSelectedPlan(response[0].id);
      }
    } catch (err) {
      console.error('Failed to load subscription plans:', err);
    }
  };

  // Step 1: Verify ABN
  const handleVerifyAbn = async () => {
    if (!abn.trim()) {
      setError('Please enter an ABN');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await api.post<AbnResult>('/agency/verify-abn', { abn });
      setAbnResult(result);

      if (result.verified) {
        setAbnVerified(true);
        if (result.entityName) {
          setAgencyName(result.entityName);
        }
      } else {
        setError(result.error || 'ABN verification failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify ABN');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Validate Email
  const handleValidateEmail = async () => {
    if (!contactEmail.trim()) return;

    try {
      const result = await api.post<{ valid: boolean; message: string }>('/agency/validate-email', { email: contactEmail });
      setEmailValid(result.valid);
      setEmailMessage(result.message);
    } catch (err) {
      setEmailValid(false);
      setEmailMessage('Failed to validate email');
    }
  };

  // Step 3: Validate Enterprise Code
  const validateEnterpriseCode = async (code: string) => {
    if (!code.trim()) {
      setDiscountResult(null);
      return;
    }

    try {
      const result = await api.post<DiscountCodeResult>('/agency/validate-discount-code', { code });
      setDiscountResult(result);
    } catch (err) {
      setDiscountResult({ valid: false, error: 'Failed to validate code' });
    }
  };

  // Step 4: Submit Registration
  const handleSubmit = async () => {
    if (!agreedToTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post<{ success: boolean; message?: string; error?: string }>('/agency/register', {
        agencyName,
        abn,
        address,
        contactName,
        contactEmail,
        contactPhone,
        password,
        subscriptionPlanId: useEnterpriseCode && discountResult?.valid ? null : selectedPlan,
        enterpriseCode: useEnterpriseCode ? enterpriseCode : null
      });

      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.error || 'Registration failed');
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = abnVerified;
  const canProceedStep2 = agencyName && address && contactName && contactEmail && emailValid;
  const canProceedStep3 = useEnterpriseCode ? discountResult?.valid : selectedPlan !== null;
  const canProceedStep4 = password.length >= 8 && password === confirmPassword && agreedToTerms;

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-secondary" />
          <h2 className="text-2xl font-bold text-primary mb-2">Registration Successful!</h2>
          <p className="text-gray-600 mb-6">
            We've sent a verification email to <strong>{contactEmail}</strong>.
            Please check your inbox and click the verification link to activate your account.
          </p>
          <div className="bg-secondary/10 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700">
              <strong>Next Steps:</strong>
              <br />1. Check your email inbox
              <br />2. Click the verification link
              <br />3. Log in to your agency dashboard
            </p>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-primary" />
          <h1 className="text-3xl font-bold text-primary mb-2">Register Your Agency</h1>
          <p className="text-gray-600">Join Rentry and streamline your property management</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step === s ? 'bg-primary text-white' :
                step > s ? 'bg-secondary text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 4 && (
                <div className={`w-12 h-1 mx-2 ${step > s ? 'bg-secondary' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-red-800">{error}</div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Step 1: ABN Verification */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-primary mb-2">Verify Your Business</h2>
                <p className="text-gray-600 text-sm">Enter your Australian Business Number (ABN) to verify your agency</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ABN *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={abn}
                    onChange={(e) => {
                      setAbn(e.target.value);
                      setAbnVerified(false);
                      setAbnResult(null);
                    }}
                    placeholder="XX XXX XXX XXX"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                  />
                  <button
                    onClick={handleVerifyAbn}
                    disabled={loading || !abn.trim()}
                    className="px-6 py-3 bg-secondary text-white rounded-lg font-medium hover:bg-secondary/90 disabled:opacity-50"
                  >
                    {loading ? <Loader className="w-5 h-5 animate-spin" /> : 'Verify'}
                  </button>
                </div>
              </div>

              {abnResult && abnResult.verified && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800">ABN Verified</p>
                      <p className="text-sm text-green-700 mt-1">
                        <strong>Entity:</strong> {abnResult.entityName}<br />
                        <strong>Type:</strong> {abnResult.entityType}<br />
                        <strong>Status:</strong> {abnResult.businessStatus}<br />
                        <strong>GST Registered:</strong> {abnResult.gstRegistered ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  disabled={!canProceedStep1}
                  className="flex items-center gap-2 px-6 py-3 bg-secondary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  Continue <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Business Details */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-primary mb-2">Business Details</h2>
                <p className="text-gray-600 text-sm">Provide your agency's contact information</p>
              </div>

              <div className="grid gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="w-4 h-4 inline mr-1" /> Agency Name *
                  </label>
                  <input
                    type="text"
                    value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" /> Business Address *
                  </label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Street, Suburb, State, Postcode"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-1" /> Contact Name *
                  </label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-1" /> Business Email *
                  </label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => {
                      setContactEmail(e.target.value);
                      setEmailValid(null);
                    }}
                    onBlur={handleValidateEmail}
                    placeholder="admin@youragency.com.au"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent ${
                      emailValid === false ? 'border-red-300' : emailValid === true ? 'border-green-300' : 'border-gray-300'
                    }`}
                  />
                  {emailMessage && (
                    <p className={`text-sm mt-1 ${emailValid ? 'text-green-600' : 'text-red-600'}`}>
                      {emailMessage}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Must be a business email (no Gmail, Yahoo, etc.)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-1" /> Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="04XX XXX XXX"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  <ChevronLeft className="w-5 h-5" /> Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!canProceedStep2}
                  className="flex items-center gap-2 px-6 py-3 bg-secondary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  Continue <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Subscription Plan */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-primary mb-2">Choose Your Plan</h2>
                <p className="text-gray-600 text-sm">Select a subscription plan based on your expected lease volume</p>
              </div>

              {/* Enterprise Code Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="useEnterpriseCode"
                  checked={useEnterpriseCode}
                  onChange={(e) => setUseEnterpriseCode(e.target.checked)}
                  className="w-4 h-4 text-secondary rounded"
                />
                <label htmlFor="useEnterpriseCode" className="text-sm text-gray-700">
                  I have an enterprise discount code
                </label>
              </div>

              {useEnterpriseCode ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enterprise Code</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={enterpriseCode}
                      onChange={(e) => setEnterpriseCode(e.target.value.toUpperCase())}
                      placeholder="ENT-XXXXXXXX"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    />
                    <button
                      onClick={() => validateEnterpriseCode(enterpriseCode)}
                      disabled={!enterpriseCode.trim()}
                      className="px-6 py-3 bg-secondary text-white rounded-lg font-medium hover:bg-secondary/90 disabled:opacity-50"
                    >
                      Validate
                    </button>
                  </div>

                  {discountResult && (
                    <div className={`mt-3 p-3 rounded-lg ${discountResult.valid ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                      {discountResult.valid ? (
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" /> Valid Enterprise Code
                          </p>
                          {discountResult.description && <p className="text-sm mt-1">{discountResult.description}</p>}
                          {discountResult.fixedPricePerLease !== undefined && (
                            <p className="text-sm mt-1">
                              Fixed rate: <strong>${discountResult.fixedPricePerLease}/lease/month</strong>
                              {discountResult.maxLeasesCap && ` (up to ${discountResult.maxLeasesCap} leases)`}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p>{discountResult.error || 'Invalid code'}</p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid gap-4">
                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        selectedPlan === plan.id ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{plan.displayName}</h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {plan.minLeases} - {plan.maxLeases || '∞'} active leases
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{plan.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-primary">${plan.pricePerLease}</p>
                          <p className="text-xs text-gray-500">per lease/month</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  <ChevronLeft className="w-5 h-5" /> Back
                </button>
                <button
                  onClick={() => setStep(4)}
                  disabled={!canProceedStep3}
                  className="flex items-center gap-2 px-6 py-3 bg-secondary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  Continue <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Admin Account */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-primary mb-2">Create Admin Account</h2>
                <p className="text-gray-600 text-sm">Set up your administrator password to complete registration</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  <strong>Email:</strong> {contactEmail}
                </p>
              </div>

              <div className="grid gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock className="w-4 h-4 inline mr-1" /> Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Minimum 8 characters"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock className="w-4 h-4 inline mr-1" /> Confirm Password *
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent ${
                      confirmPassword && password !== confirmPassword ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-sm text-red-600 mt-1">Passwords do not match</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="w-4 h-4 text-secondary rounded mt-0.5"
                />
                <label htmlFor="agreeTerms" className="text-sm text-gray-700">
                  I agree to the <a href="/terms" className="text-secondary hover:underline">Terms of Service</a> and{' '}
                  <a href="/privacy" className="text-secondary hover:underline">Privacy Policy</a>
                </label>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(3)}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  <ChevronLeft className="w-5 h-5" /> Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!canProceedStep4 || loading}
                  className="flex items-center gap-2 px-6 py-3 bg-secondary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" /> Creating Account...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" /> Complete Registration
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Already have an account */}
        <div className="text-center mt-6">
          <p className="text-gray-600">
            Already have an account?{' '}
            <button onClick={() => navigate('/login')} className="text-secondary font-medium hover:underline">
              Log in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}


